import { useCallback, useEffect } from 'react'
import type { AttachedFile } from '../../../types/chatCache.types'
import { useChatStore } from '../../../stores/chatStore'


/**
 * Hook to manage file attachments
 * Handles adding, removing, and uploading files via Zustand store
 */
export const useChatFiles = (agentId: string | null) => {
    const store = useChatStore()

    // Select state from store
    // Use callback for selector stability
    // Select just the agent state to avoid creating new array references in the selector
    // This fixes "The result of getSnapshot should be cached to avoid an infinite loop"
    const agentState = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId] : undefined
        , [agentId]))

    const attachedFiles = agentState?.attachedFiles || []

    // Unified file processor for both manual selection and drag-and-drop
    const processAndAttachFiles = useCallback(async (files: FileList | File[] | AttachedFile[]) => {
        if (!agentId) return

        const fileArray = Array.isArray(files) ? files : Array.from(files)

        for (const file of fileArray) {
            // 1. If it's already an AttachedFile (e.g. from store/direct), just add it
            if ('id' in file && 'blobUrl' in file) {
                store.addAttachedFile(agentId, file as AttachedFile)
                continue
            }

            // 2. It's a native File object
            const nativeFile = file as File

            // Industrial Validation
            if (nativeFile.size > 10 * 1024 * 1024) {
                console.error(`File ${nativeFile.name} exceeds 10MB limit.`)
                // Toast notification could be triggered here in production
                continue
            }

            // Create high-performance Object URL for preview
            const blobUrl = URL.createObjectURL(nativeFile)

            const newFile: AttachedFile = {
                id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
                name: nativeFile.name,
                type: nativeFile.type,
                size: nativeFile.size,
                blobUrl: blobUrl,
                file: nativeFile, // Keep binary for upload
                uploadProgress: 0,
                uploadStatus: 'idle' as const
            }

            store.addAttachedFile(agentId, newFile)
        }
    }, [agentId, store])

    // Specific wrapper for single file attachments
    const attachFile = useCallback((file: File | AttachedFile) => {
        processAndAttachFiles([file as any])
    }, [processAndAttachFiles])

    // Cleanup Effect: Revoke Object URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (attachedFiles.length > 0) {
                attachedFiles.forEach(f => {
                    if (f.blobUrl) URL.revokeObjectURL(f.blobUrl)
                })
            }
        }
    }, [attachedFiles])

    // Remove a file from attachments with manual cleanup
    const removeFile = useCallback((fileId: string) => {
        if (!agentId) return
        const file = attachedFiles.find(f => f.id === fileId)
        if (file?.blobUrl) URL.revokeObjectURL(file.blobUrl)
        store.removeAttachedFile(agentId, fileId)
    }, [agentId, store, attachedFiles])

    // Clear all attachments with manual cleanup
    const clearFiles = useCallback(() => {
        if (!agentId) return
        attachedFiles.forEach(f => {
            if (f.blobUrl) URL.revokeObjectURL(f.blobUrl)
        })
        store.clearAttachedFiles(agentId)
    }, [agentId, store, attachedFiles])

    // Update file upload progress
    const updateFileProgress = useCallback((fileId: string, progress: number) => {
        if (!agentId) return
        const currentFiles = store.agents[agentId]?.attachedFiles || []
        const newFiles = currentFiles.map(f =>
            f.id === fileId ? { ...f, uploadProgress: progress, uploadStatus: 'uploading' as const } : f
        )
        store.setAttachedFiles(agentId, newFiles)
    }, [agentId, store])

    const markFileComplete = useCallback((fileId: string) => {
        if (!agentId) return
        const currentFiles = store.agents[agentId]?.attachedFiles || []
        const newFiles = currentFiles.map(f =>
            f.id === fileId ? { ...f, uploadProgress: 100, uploadStatus: 'uploaded' as const } : f
        )
        store.setAttachedFiles(agentId, newFiles)
    }, [agentId, store])

    const markFileError = useCallback((fileId: string) => {
        if (!agentId) return
        const currentFiles = store.agents[agentId]?.attachedFiles || []
        const newFiles = currentFiles.map(f =>
            f.id === fileId ? { ...f, uploadStatus: 'error' as const } : f
        )
        store.setAttachedFiles(agentId, newFiles)
    }, [agentId, store])

    // Setter for compatibility
    const setAttachedFiles = useCallback((files: AttachedFile[]) => {
        if (agentId) store.setAttachedFiles(agentId, files)
    }, [agentId, store])

    return {
        attachedFiles,
        attachFile,
        processAndAttachFiles,
        removeFile,
        clearFiles,
        updateFileProgress,
        markFileComplete,
        markFileError,
        setAttachedFiles
    }
}
